import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Trash2, Sparkles } from "lucide-react"; 
import logo_up from "../../assets/images/Logo for website/Desktop.png";
import AIProductSlider from "./AIProductSlider";

// API Base URL
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Initial state
const initialMessage = {
  id: 1,
  text: "Hello! I'm your MASHO AI.\nTell me your mood, occasion, budget, or favorite notes — I'll find your perfect scent.",
  sender: "ai",
};

export default function MashoAIChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([initialMessage]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleClearChat = () => {
    setMessages([initialMessage]);
  };

  const handleSend = async (text = input) => {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: text,
      sender: "user",
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);

    try {
      // Call AI Recommendations API
      const response = await fetch(`${API_BASE_URL}/masho-ai/get-recommendations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_message: text
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get AI recommendations');
      }

      const aiData = await response.json();
      
      // The API returns an array with one object
      if (aiData && aiData.length > 0) {
        const { response_message, suggestions } = aiData[0];
        
        // Extract product_ids and gift_ids from suggestions
        const productIds = suggestions && suggestions.length > 0 ? suggestions[0].product_ids || [] : [];
        const giftIds = suggestions && suggestions.length > 0 ? suggestions[0].gift_ids || [] : [];
        
        // Create AI response message with IDs (let slider handle fetching)
        const aiResponse = {
          id: Date.now() + 1,
          sender: "ai",
          text: response_message || "Based on your taste, I've curated these selections for you:",
          productIds: productIds,
          giftIds: giftIds,
          hasRecommendations: productIds.length > 0 || giftIds.length > 0
        };

        setMessages((prev) => [...prev, aiResponse]);
      } else {
        // Fallback response if no suggestions
        const fallbackResponse = {
          id: Date.now() + 1,
          sender: "ai",
          text: "I'm sorry, I couldn't find any specific recommendations for that request. Could you provide more details about what you're looking for?",
        };
        setMessages((prev) => [...prev, fallbackResponse]);
      }
    } catch (error) {
      console.error('Error getting AI recommendations:', error);
      
      // Error response
      const errorResponse = {
        id: Date.now() + 1,
        sender: "ai",
        text: "I apologize, but I'm having trouble processing your request right now. Please try again in a moment.",
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const quickPrompts = [
    "Romantic wedding perfume",
    "Fresh summer scent",
    "Gift under ₹2000",
    "Rich & Mysterious Oud",
  ];

  return (
    <>
      {/* --- Launcher Button --- */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-[60] group"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {/* --- Tooltip: Ask Masho AI --- */}
        <span 
          className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-4 py-2 bg-white text-gray-800 text-sm font-semibold rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.1)] border border-[#D4AF37]/20 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap translate-x-2 group-hover:translate-x-0 hidden md:block"
          style={{ fontFamily: "Montserrat, serif" }}
        >
          Ask Masho AI
        </span>

        <div className="relative">
          <div className={`relative flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all duration-300 ${isOpen ? 'bg-[#D4AF37]' : 'bg-white border-2 border-[#D4AF37]/20'}`}>
            <AnimatePresence mode="wait">
              {isOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                >
                  <X className="w-8 h-8 text-white" />
                </motion.div>
              ) : (
                <motion.div
                  key="logo"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  className="p-2"
                >
                  <img src={logo_up} alt="AI" className="w-full h-full object-contain drop-shadow-md" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {!isOpen && (
            <span className="absolute -inset-1 rounded-full border border-[#D4AF37]/40 animate-ping opacity-75" />
          )}
        </div>
      </motion.button>

      {/* --- Chat Window --- */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="fixed bottom-28 right-4 md:right-6 z-50 w-[95vw] md:w-[380px] h-[600px] max-h-[80vh] 
                        bg-white rounded-2xl shadow-2xl border border-[#D4AF37]/20 overflow-hidden flex flex-col"
          >
            
            {/* --- Header --- */}
            <div className="relative bg-gradient-to-br from-[#D4AF37] via-[#C5A028] to-[#B08927] p-4 text-white shadow-lg z-10 overflow-hidden">
               <div className="absolute inset-0 opacity-10 pointer-events-none" 
                   style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '20px 20px' }}>
               </div>
               <div className="relative flex items-center justify-between">
                 <div className="flex items-center gap-3">
                   {/* Logo Icon */}
                   <div className="p-1.5 bg-white rounded-full backdrop-blur-sm shadow-inner border border-white/10 w-10 h-10 flex items-center justify-center">
                      <img 
                        src={logo_up} 
                        alt="AI Logo" 
                        className="w-full h-full object-contain" 
                      />
                   </div>
                   <div>
                      {/* Font: Losta Masta */}
                      <h3 className="font-bold text-lg leading-none tracking-wide text-white drop-shadow-sm" 
                          style={{ fontFamily: "Losta Masta, serif" }}>
                        MASHO AI
                      </h3>
                      <p className="text-[10px] uppercase tracking-widest opacity-90 font-medium mt-1">AI Assistant</p>
                   </div>
                 </div>
                 {messages.length > 1 && (
                   <button
                     onClick={handleClearChat}
                     className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors text-white border border-white/20 backdrop-blur-sm"
                   >
                     <Trash2 className="w-4 h-4" />
                   </button>
                 )}
               </div>
            </div>

            {/* --- Messages Area --- */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-hide bg-[#FAFAFA]">
              {messages.map((msg, index) => {
                const isFirstMessage = index === 0;
                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex w-full ${isFirstMessage ? "justify-center my-4" : msg.sender === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`relative px-5 py-3.5 shadow-sm text-sm leading-relaxed
                        ${isFirstMessage 
                            ? "bg-white text-center text-gray-600 border border-[#D4AF37]/20 max-w-[90%] rounded-2xl shadow-sm" 
                            : msg.sender === "user"
                              ? "bg-[#D4AF37] text-black font-medium max-w-[85%] rounded-2xl rounded-tr-sm"
                              : "bg-white text-gray-800 border border-[#D4AF37]/10 max-w-[85%] rounded-2xl rounded-tl-sm"
                        }`}
                    >
                      <p className="whitespace-pre-wrap" style={{ fontFamily: "Montserrat, serif" }}>{msg.text}</p>
                      
                      {msg.hasRecommendations && (
                        <div className="mt-4 -mx-2">
                          <AIProductSlider 
                            productIds={msg.productIds || []} 
                            giftIds={msg.giftIds || []} 
                          />
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}

              {/* --- CENTERED QUICK PROMPTS --- */}
              {messages.length === 1 && !isTyping && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="px-4 mt-2"
                >
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="h-px bg-[#D4AF37]/20 w-12"></div>
                    <span className="text-[10px] uppercase tracking-widest text-gray-400 font-medium flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-[#D4AF37]" /> Suggestions
                    </span>
                    <div className="h-px bg-[#D4AF37]/20 w-12"></div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {quickPrompts.map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSend(prompt)}
                        className="p-3 text-xs text-center border border-[#D4AF37]/20 bg-white rounded-xl 
                                   text-gray-600 hover:bg-[#D4AF37]/5 hover:border-[#D4AF37] hover:text-[#D4AF37] hover:shadow-sm
                                   transition-all duration-300 active:scale-95 flex flex-col items-center justify-center gap-1 h-20"
                        style={{ fontFamily: "Montserrat, serif" }}
                      >
                          <span className="line-clamp-2">{prompt}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                    <div className="bg-white border border-[#D4AF37]/10 px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm flex gap-1">
                      <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce [animation-delay:-0.3s]" />
                      <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce [animation-delay:-0.15s]" />
                      <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" />
                    </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* --- Footer (Input Only) --- */}
            <div className="p-4 bg-white border-t border-[#D4AF37]/10">
              <div className="relative flex items-center gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && !isTyping && handleSend()}
                  placeholder="Ask about perfumes..."
                  disabled={isTyping}
                  className="w-full bg-gray-50 text-gray-900 text-sm rounded-xl pl-4 pr-12 py-3.5 
                             focus:outline-none focus:ring-1 focus:ring-[#D4AF37] border border-[#D4AF37]/20 transition-all placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "Montserrat, serif" }}
                />
                
                <button
                  onClick={() => handleSend()}
                  disabled={!input.trim() || isTyping}
                  className="absolute right-2 p-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#c19b2e] 
                             disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}